import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import * as Sentry from '@sentry/angular'
import { combineLatest, of } from 'rxjs'
import { catchError, concatMap, map, tap } from 'rxjs/operators'
import { environment } from '../../../environments/environment'
import * as AuthActions from './auth.actions'
import { AuthService } from './auth.service'

@Injectable()
export class AuthEffects {
  constructor(private actions$: Actions, private auth: AuthService, private router: Router) {}

  autologin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.autologin),
      concatMap(() =>
        this.auth.autologin().pipe(
          map((output) =>
            output
              ? AuthActions.loginSuccess({ output, interactive: false })
              : AuthActions.loginFailure({ error: output })
          ),
          catchError((error) => of(AuthActions.loginFailure({ reason: error.reason, error })))
        )
      )
    )
  )

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      concatMap((action) =>
        this.auth.login(action.credentials).pipe(
          map((output) => AuthActions.loginSuccess({ output, interactive: true })),
          catchError((error) => of(AuthActions.loginFailure({ error })))
        )
      )
    )
  )

  signup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.signup),
      concatMap((action) =>
        this.auth.signup(action.credentials).pipe(
          tap(() => {
            const x = new URLSearchParams()
            x.set('mode', 'signup')
            x.set('email', action.credentials.email)
            this.router.navigateByUrl(`/auth/email?${x.toString()}`)
          }),
          map(
            () => AuthActions.signupSuccess()
            // NOTE: uncomment next lines to enable autologin
            // AuthActions.login({
            //   credentials: {
            //     email: action.credentials.email,
            //     password: action.credentials.password,
            //   },
            // })
          ),
          catchError((error) => of(AuthActions.signupFailure({ error })))
        )
      )
    )
  )

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap((action) => {
          if (environment.sentry && action.output.user) {
            Sentry.setUser({
              id: action.output.user._id,
              username: action.output.user._id,
              // NOTE: we don't share users data with sentry
              // email: action.output.user.email,
              // username: compact([action.output.user.firstname, action.output.user.lastname]).join(' '),
            })
          }

          if (action.interactive) this.router.navigateByUrl('/')
        })
      ),
    { dispatch: false }
  )

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logout),
        tap(() => {
          this.auth.logout()
          this.router.navigateByUrl('/auth/login')
        })
      ),
    { dispatch: false }
  )

  updateSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.updateSettings),
      concatMap(({ me, partner }) => {
        const queue = partner ? [this.auth.updateMe(me), this.auth.updatePartner(partner)] : [this.auth.updateMe(me)]
        return combineLatest(queue).pipe(
          map(() => AuthActions.updateSettingsSuccess()),
          catchError((error) => of(AuthActions.updateSettingsFailure({ error })))
        )
      })
    )
  )
}
